import { useEffect, useMemo, useRef, type ReactElement, type ReactNode } from 'react';
import { useMe } from '../api/me';
import { useUserJobsSubscription } from '../api/userJobsSubscription';
import { LiveStatusContext } from './liveStatusContext';
import { useToast } from './toastContext';

// Owns the ONE app-wide jobs SSE subscription and broadcasts its state (see
// liveStatusContext.ts for why this is a context and not a per-consumer hook). Also owns the
// transient-degradation toast; the persistent banner is rendered by the shell from the same
// context. A1EE-364.

// How long the channel must stay down before we say anything. The ticket asks for a signal of
// "degradation, not just a single blip", and a bare `status === 'reconnecting'` fires on every
// one-second network hiccup that the browser silently repairs — which would make the toast
// noise the user learns to ignore. Below this, the RefreshPill's quiet "Reconnecting…" is the
// whole notification, exactly as before.
const RECONNECT_TOAST_DELAY_MS = 2_500;

const RECONNECT_MESSAGE = 'Live updates interrupted — reconnecting…';

export function LiveStatusProvider({ children }: { children: ReactNode }): ReactElement {
  const { data: me } = useMe();
  const { status, failure } = useUserJobsSubscription(me?.id);
  const { showToast, dismissToast } = useToast();

  // Whether the channel has EVER been live in this session. Gates the toast so a slow first
  // connect (cold pod, a page opened before the backend is up) doesn't announce an
  // "interruption" of something that was never running — mirroring the `everSucceeded`
  // gating in `subscribeSse` for the same reason (A1EE-361).
  const wasLive = useRef(false);
  const toastId = useRef<number | null>(null);

  useEffect(() => {
    if (status === 'live') wasLive.current = true;
  }, [status]);

  useEffect(() => {
    const retract = () => {
      if (toastId.current !== null) {
        dismissToast(toastId.current);
        toastId.current = null;
      }
    };

    // Anything other than "still down" means the vague "reconnecting…" is no longer the best
    // thing on screen: on recovery there is nothing to report, on pause the tab is hidden
    // anyway, and on a hard failure the banner says something concrete. Retracting rather
    // than letting the 7s TTL run out is acceptance criterion 3.
    if (status !== 'reconnecting' || failure) {
      retract();
      return;
    }
    // One toast per degradation episode, by design. The toast's own TTL may expire while the
    // channel is still down, and we deliberately do NOT re-raise it — a sustained outage
    // would otherwise nag every 7s. The RefreshPill stays on "Reconnecting…" for the
    // duration, and a genuine hard failure escalates to the banner, which does persist.
    if (!wasLive.current || toastId.current !== null) return;

    const timer = setTimeout(() => {
      // Re-check nothing raced us while the timer was pending: the effect re-runs (and
      // clears this timer) on every status change, so reaching here means we are still down.
      toastId.current = showToast(RECONNECT_MESSAGE, 'error');
    }, RECONNECT_TOAST_DELAY_MS);
    return () => clearTimeout(timer);
  }, [status, failure, showToast, dismissToast]);

  const value = useMemo(() => ({ status, failure }), [status, failure]);

  return <LiveStatusContext.Provider value={value}>{children}</LiveStatusContext.Provider>;
}
